import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { useAssetTypeByCode } from '../../hooks/useQueries'
import type { SourceRef } from './primitives'
import type { SpaceCatalogEntry } from './spacesCatalog'

/**
 * "Parties communes" / "Espaces extérieurs" reconciliation, on existing
 * endpoints only (Option A):
 *   - GET /assets?asset_type_code=common_area|exterior_space|equipment
 *   - POST /assets/approve  (confirm to_approve spaces/equipment)
 *   - PATCH /assets/{uid}   (frontend_data markers: dr_pending, dr_equip_state)
 *
 * Equipment is never floating — it is a child asset whose parent is a space.
 */

export type EquipmentState = 'found' | 'ask' | 'confirmed' | 'absent'

export type SpaceEquipment = {
  uid: string
  name: string
  state: EquipmentState
  toApprove: boolean
  frontendData: Record<string, unknown>
  source?: SourceRef
}

export type ReliabilitySpace = {
  uid: string
  name: string
  level?: string
  pending: boolean
  toApprove: boolean
  frontendData: Record<string, unknown>
  equipment: SpaceEquipment[]
  /**
   * Always resolvable (spec §1.2: "no value renders without a resolvable
   * source. No exceptions."). A document-sourced space (dr_source in
   * frontend_data) resolves normally; a socle-created space (§7.5 — no
   * source by design, system-proposed) resolves to an explicit manual-style
   * label rather than rendering nothing.
   */
  source: SourceRef
}

/**
 * Label for a space with no document/manual source — the default space
 * socle (spec §7.5), created system-side on building creation. Matches the
 * existing (untranslated) 'Saisie' convention in useReliability.ts's
 * sourceFromValue rather than introducing a half-translated one; SourceChip
 * renders manualLabel verbatim regardless.
 */
const SYSTEM_SOCLE_LABEL = 'Créé automatiquement'

export type SpacesKind = 'common' | 'exterior'

const KIND_TYPE: Record<SpacesKind, string> = { common: 'common_area', exterior: 'exterior_space' }

type RawAsset = {
  uid: string
  name?: string
  parent_asset_uid?: string
  flags?: string[]
  frontend_data?: Record<string, unknown>
}

function listAssets(org: string, buildingUid: string, typeCode: string) {
  return apiClient
    .GET('/api/v1/organizations/{orgCode}/assets', {
      params: {
        path: { orgCode: org },
        query: { ancestor_uid: buildingUid, asset_type_code: typeCode, include: ['flags', 'frontend_data'], limit: 500 } as never,
      },
    })
    .then((r) => ((r.data as { data?: RawAsset[] })?.data ?? []))
}

function srcOf(fd: Record<string, unknown>): SourceRef | undefined {
  const s = fd.dr_source as { source?: string; document_filename?: string; document_uid?: string; page?: number | string } | undefined
  if (!s) return undefined
  if (s.source === 'manual') return { manualLabel: 'Saisie' }
  return { documentUid: s.document_uid, documentName: s.document_filename, page: s.page }
}

function equipState(a: RawAsset): EquipmentState {
  const fd = a.frontend_data ?? {}
  const explicit = fd.dr_equip_state as EquipmentState | undefined
  if (explicit) return explicit
  if (fd.dr_ask === true) return 'ask'
  if ((a.flags ?? []).includes('to_approve')) return 'found'
  return 'confirmed'
}

export function useSpaces(org: string, buildingUid: string, kind: SpacesKind) {
  const queryClient = useQueryClient()
  const typeCode = KIND_TYPE[kind]
  const spacesKey = ['dr-spaces', org, buildingUid, kind] as const
  const equipKey = ['dr-equipment', org, buildingUid] as const
  const { data: equipmentType } = useAssetTypeByCode(org, 'equipment')
  const { data: spaceType } = useAssetTypeByCode(org, typeCode)

  const spacesQ = useQuery({
    queryKey: spacesKey,
    queryFn: () => listAssets(org, buildingUid, typeCode),
    enabled: !!org && !!buildingUid,
  })
  const equipQ = useQuery({
    queryKey: equipKey,
    queryFn: () => listAssets(org, buildingUid, 'equipment'),
    enabled: !!org && !!buildingUid,
  })

  const spaces: ReliabilitySpace[] = useMemo(() => {
    const byParent = new Map<string, RawAsset[]>()
    for (const e of equipQ.data ?? []) {
      const k = e.parent_asset_uid || ''
      if (!byParent.has(k)) byParent.set(k, [])
      byParent.get(k)!.push(e)
    }
    return (spacesQ.data ?? []).map((s) => {
      const fd = s.frontend_data ?? {}
      const toApprove = (s.flags ?? []).includes('to_approve')
      return {
        uid: s.uid,
        name: s.name || '—',
        level: fd.dr_level as string | undefined,
        pending: toApprove || fd.dr_pending === true,
        toApprove,
        frontendData: fd,
        // No dr_source and no to_approve => system-proposed (the space
        // socle, §7.5) rather than extracted — resolve to an explicit
        // manual-style label instead of leaving the source unresolved.
        source: srcOf(fd) ?? { manualLabel: SYSTEM_SOCLE_LABEL },
        equipment: (byParent.get(s.uid) ?? []).map((e) => ({
          uid: e.uid,
          name: e.name || '—',
          state: equipState(e),
          toApprove: (e.flags ?? []).includes('to_approve'),
          frontendData: e.frontend_data ?? {},
          source: srcOf(e.frontend_data ?? {}),
        })),
      }
    })
  }, [spacesQ.data, equipQ.data])

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dr-spaces', org, buildingUid, kind] })
    queryClient.invalidateQueries({ queryKey: ['dr-equipment', org, buildingUid] })
  }, [queryClient, org, buildingUid, kind])

  const patchFrontendData = useCallback(
    async (uid: string, current: Record<string, unknown>, patch: Record<string, unknown>) => {
      await apiClient.PATCH('/api/v1/organizations/{orgCode}/assets/{assetUid}', {
        params: { path: { orgCode: org, assetUid: uid } },
        body: { frontend_data: { ...current, ...patch } } as never,
      })
    },
    [org],
  )

  const approve = useCallback(
    async (uid: string, action: 'approve' | 'reject') => {
      await apiClient.POST('/api/v1/organizations/{orgCode}/assets/approve', {
        params: { path: { orgCode: org } },
        body: { uids: [uid], action } as never,
      })
    },
    [org],
  )

  /** Set an equipment present (confirmed) or absent. */
  const setEquipmentState = useCallback(
    async (eq: SpaceEquipment, state: 'confirmed' | 'absent') => {
      if (eq.toApprove) await approve(eq.uid, state === 'confirmed' ? 'approve' : 'reject')
      await patchFrontendData(eq.uid, eq.frontendData, { dr_equip_state: state, dr_ask: false })
      invalidate()
    },
    [approve, patchFrontendData, invalidate],
  )

  /** Confirm a whole space. */
  const confirmSpace = useCallback(
    async (space: ReliabilitySpace) => {
      if (space.toApprove) await approve(space.uid, 'approve')
      await patchFrontendData(space.uid, space.frontendData, { dr_pending: false })
      invalidate()
    },
    [approve, patchFrontendData, invalidate],
  )

  /**
   * Manually add an equipment to a space (spec §7.3's "Ajouter un équipement").
   * A manual add is inherently a human decision, so it starts `confirmed`
   * (never `found`/`ask`, which are extraction-only states). Its source
   * resolves to "Saisie" (spec §6.1's manual Source variant) via
   * dr_source.source: 'manual' — every rendered value must resolve to a
   * source (§1.2), and a manual add is exactly that variant, not an
   * exception to it.
   */
  const addEquipment = useCallback(
    async (space: ReliabilitySpace, name: string) => {
      if (!equipmentType?.uid || !name.trim()) return
      await apiClient.POST('/api/v1/organizations/{orgCode}/assets', {
        params: { path: { orgCode: org } },
        body: {
          name: name.trim(),
          asset_type_uid: equipmentType.uid,
          parent_asset_uid: space.uid,
          frontend_data: { dr_equip_state: 'confirmed', dr_source: { source: 'manual' } },
        },
      })
      invalidate()
    },
    [org, equipmentType, invalidate],
  )

  /**
   * The site's `outdoor` entity, if one exists (spec 2026-09-13-05 §3) — only
   * meaningful for `kind === 'exterior'`, where `buildingUid` is actually the
   * site's uid (see `BuildingConfigWizard.tsx`'s `assetUid={buildingGroupUid}`
   * mount). Resolved lazily, on the create call itself, rather than kept as
   * hook state: this hook creates no `outdoor` asset itself (§0 — the entity
   * is proposed by the wizard checkbox only), it merely prefers one when it
   * is already there.
   */
  const resolveOutdoorParentUid = useCallback(async (): Promise<string | undefined> => {
    if (kind !== 'exterior') return undefined
    const res = await apiClient.GET('/api/v1/organizations/{orgCode}/assets', {
      params: {
        path: { orgCode: org },
        query: { ancestor_uid: buildingUid, asset_type_code: 'outdoor', sort: 'created_at', limit: 1 } as never,
      },
    })
    const list = (res.data as { data?: RawAsset[] })?.data ?? []
    return list[0]?.uid
  }, [org, buildingUid, kind])

  /**
   * Confirm a typical-space *suggestion* (spec
   * 2026-07-09-remove-space-socle-auto-creation): the catalog no longer
   * persists a speculative socle on building creation — the space is created
   * only here, when the user checks the suggestion. Checking IS the
   * confirmation, so the asset is created **confirmed** (no `dr_pending`) with a
   * manual source (`dr_source.source: 'manual'` → "Saisie"), matching the
   * manual-add convention of `addEquipment`. It then behaves exactly like any
   * real space. Interior suggestions carry their catalog `level` via `dr_level`.
   *
   * Exterior suggestions prefer the site's `outdoor` entity when there is one
   * (spec 2026-09-13-05 §3); when there is none, they parent to the site
   * exactly as before that spec.
   */
  const createSpaceFromSuggestion = useCallback(
    async (entry: SpaceCatalogEntry) => {
      if (!spaceType?.uid) return
      const frontendData: Record<string, unknown> = { dr_source: { source: 'manual' } }
      if (entry.level) frontendData.dr_level = entry.level
      const outdoorParentUid = await resolveOutdoorParentUid()
      await apiClient.POST('/api/v1/organizations/{orgCode}/assets', {
        params: { path: { orgCode: org } },
        body: {
          name: entry.name,
          asset_type_uid: spaceType.uid,
          parent_asset_uid: outdoorParentUid ?? buildingUid,
          frontend_data: frontendData,
        } as never,
      })
      invalidate()
    },
    [org, buildingUid, spaceType, invalidate, resolveOutdoorParentUid],
  )

  return {
    spaces,
    isLoading: spacesQ.isLoading || equipQ.isLoading,
    setEquipmentState,
    confirmSpace,
    addEquipment,
    createSpaceFromSuggestion,
    canCreateSpace: !!spaceType?.uid,
    canAddEquipment: !!equipmentType?.uid,
  }
}
